import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { ApiDataBinding, withApiData } from 'react-api-data';
import { ApiResponse, SpeedAndInterfaceType } from 'constants/api';
import { compose } from 'recompose';
import { SortOrder, NetworkRequestStatus } from 'constants/literals';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import ViewContainer from 'components/utils/ViewContainer';
import { Row, Col, Input, Card, CardHeader, CardBody } from 'reactstrap';
import SyncButton from 'components/utils/SyncButton';
import InteractiveRemoteTable from 'components/utils/InteractiveRemoteTable';
import { get, debounce } from 'lodash';
import { Link } from 'react-router-dom';
import { getLinkToSpeedAndInterfaceType } from 'utils/linksUtils';
import SpeedAndInterfaceTypeAddModal from 'components/modals/SpeedAndInterfaceTypeAddModal';

const SIZE_PER_PAGE = 10;
const DEFAULT_SORT_FIELD: keyof SpeedAndInterfaceType = 'name';
const DEFAULT_SORT_ORDER: SortOrder = 'asc';

interface ConnectProps {
    speedAndInterfaceTypesBinding: ApiDataBinding<ApiResponse<{ speedAndInterfaceTypes: SpeedAndInterfaceType }>>;
}

type Props = ConnectProps;

const enhance = compose<ConnectProps, {}>(
    withApiData(
        {
            speedAndInterfaceTypesBinding: 'getSpeedAndInterfaceTypes'
        },
        () => ({
            speedAndInterfaceTypesBinding: {
                page: 1,
                per_page: SIZE_PER_PAGE,
                sort: DEFAULT_SORT_FIELD,
                order: DEFAULT_SORT_ORDER
            }
        })
    )
);

const SpeedAndInterfaceTypesView: FunctionComponent<Props> = ({ speedAndInterfaceTypesBinding }) => {
    const { t } = useTranslation();

    const fetchSpeedAndInterfaceTypes = useCallback(
        (params?: object) => {
            speedAndInterfaceTypesBinding.perform({
                ...speedAndInterfaceTypesBinding.request.params,
                ...params
            })
        },
        [speedAndInterfaceTypesBinding]
    );

    const filter = (search: string) => {
        fetchSpeedAndInterfaceTypes({ page: 1, search_for: search });
    };

    const debouncedFilter = debounce(filter, 500);

    const tableColumns = useMemo(
        () => [
            {
                dataField: 'name',
                text: t('speedAndInterfaceType.name'),
                sort: true
            },
            {
                dataField: 'speed',
                text: t('speedAndInterfaceType.speed'),
                sort: true,
                formatter: (_cell: any, row: SpeedAndInterfaceType) => (
                    `${row.speed/1000}G`
                )
            },
            {
                dataField: 'interface_type',
                text: t('speedAndInterfaceType.interface_type'),
                sort: true
            },
            {
                dataField: 'fibre',
                text: t('speedAndInterfaceType.fibre'),
                sort: true
            },
            {
                dataField: 'link',
                isDummyField: true,
                text: t('common.link'),
                formatter: (_cell: any, row: SpeedAndInterfaceType) => (
                    <Link to={getLinkToSpeedAndInterfaceType(row.uuid)}>{t('common.view')}</Link>
                )
            }
        ],
        [t]
    );

    return (
        <>
            <Helmet title={t('speedAndInterfaceTypes.pageTitle')} />
            <ViewContainer>
                <Row>
                    <Col>
                        <div className="d-flex justify-content-between">
                            <div className="d-flex align-items-center">
                                <h2>{t('speedAndInterfaceTypes.pageTitle')}</h2>
                                <SyncButton
                                    className="ml-2"
                                    title={t('speedAndInterfaceTypes.refreshButton')}
                                    onClick={fetchSpeedAndInterfaceTypes}
                                />
                            </div>

                            <SpeedAndInterfaceTypeAddModal />
                        </div>

                        <hr />

                        <Card className="card-accent-primary">
                            <CardHeader>
                                <Row className="justify-content-between">
                                    <Col xl={3}>
                                        <Input
                                            defaultValue=''
                                            onChange={(e) => debouncedFilter(e.target.value)}
                                            className="form-control"
                                            placeholder={t('table.searchInputPlaceholder')}
                                        />
                                    </Col>
                                </Row>
                            </CardHeader>

                            <CardBody>
                                <InteractiveRemoteTable
                                    keyField="uuid"
                                    columns={tableColumns}
                                    data={get(speedAndInterfaceTypesBinding, 'data.data.speed_and_interface_types', [])}
                                    paginationOptions={{
                                        page: get(speedAndInterfaceTypesBinding, 'data.meta.page', 1),
                                        sizePerPage: get(speedAndInterfaceTypesBinding, 'data.meta.page_size', SIZE_PER_PAGE),
                                        totalSize: get(speedAndInterfaceTypesBinding, 'data.meta.total_items', 0)
                                    }}
                                    defaultSorted={{
                                        dataField: DEFAULT_SORT_FIELD,
                                        order: DEFAULT_SORT_ORDER
                                    }}
                                    loading={speedAndInterfaceTypesBinding.request.networkStatus === NetworkRequestStatus.Loading}
                                    fetchPage={(pageNumber, pageSize, sortField, sortOrder) => (
                                        fetchSpeedAndInterfaceTypes({
                                            page: pageNumber,
                                            per_page: pageSize,
                                            sort: sortField,
                                            order: sortOrder
                                        })
                                    )}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </ViewContainer>
        </>
    );
};

export default enhance(SpeedAndInterfaceTypesView);
